import { zodResolver } from "@hookform/resolvers/zod";
import { SubscriptionStatusEnum } from "@nlc/shared-types";
import { useId } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import {
  ClearableInput,
  FieldError,
  Label,
  TextField,
} from "@/components/field";
import { Modal } from "@/components/modal";
import { dateFormatter } from "@/utils/dateFormatter";

import { formatCurrency } from "../../utils/format-currency";
import { BillingNotice } from "../components/BillingNotice";
import { useBillingAccountOverview } from "../overview/hooks/useBillingAccountOverview";
import {
  CreditNoteField,
  CreditReasonSelect,
} from "./components/CreditReasonFields";
import { useAdjustRenewalDate } from "./hooks/useAdjustRenewalDate";
import {
  type AdjustRenewalDateFormInput,
  adjustRenewalDateFormSchema,
  type AdjustRenewalDateFormValues,
  daysToAddSchema,
} from "./utils/adjust-renewal-date-schema";
import { addRenewalDays } from "./utils/renewal-date";

const BLOCKED_STATUS_MESSAGES: Partial<Record<SubscriptionStatusEnum, string>> =
  {
    [SubscriptionStatusEnum.CANCELED]:
      "This account is canceled, so there's no renewal date to adjust.",
    [SubscriptionStatusEnum.DUNNING_PROCESS]:
      "This account is in dunning. Adjust the renewal date once the payment is settled.",
    [SubscriptionStatusEnum.PENDING_CANCELLATION]:
      "This account has a pending cancellation, so its end date can't change.",
  };

interface AdjustRenewalDateModalProps {
  readonly onClose: () => void;
  readonly projectId: number;
}

export function AdjustRenewalDateModal({
  onClose,
  projectId,
}: AdjustRenewalDateModalProps) {
  const formId = useId();
  const { data: overview } = useBillingAccountOverview(projectId);
  const adjustRenewalDate = useAdjustRenewalDate(projectId);
  const { control, formState, handleSubmit, setValue, watch } = useForm<
    AdjustRenewalDateFormInput,
    unknown,
    AdjustRenewalDateFormValues
  >({
    defaultValues: { days: "", note: "" },
    mode: "onChange",
    resolver: zodResolver(adjustRenewalDateFormSchema),
  });

  const blockedMessage =
    overview && BLOCKED_STATUS_MESSAGES[overview.subscriptionStatus];
  const parsedDays = daysToAddSchema.safeParse(watch("days"));
  const daysToAdd = parsedDays.success ? parsedDays.data : null;

  const submit = (values: AdjustRenewalDateFormValues) =>
    adjustRenewalDate.mutate(values, { onSuccess: onClose });

  return (
    <Modal
      classNames={{ modal: "flex h-full w-full flex-col sm:h-auto sm:w-144" }}
      isDismissable
      isOpen
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <Dialog className="h-full">
        <DialogCloseButton />
        <DialogHeader className="px-xl pt-2xl text-lg font-semibold sm:border-b sm:border-border-secondary sm:px-3xl sm:pb-2xl sm:pt-3xl">
          Adjust renewal date
        </DialogHeader>

        <DialogBody className="px-xl pt-xl sm:px-3xl sm:py-2xl">
          {blockedMessage ? (
            <BillingNotice description={blockedMessage} />
          ) : (
            <form
              className="flex flex-col gap-xl"
              id={formId}
              onSubmit={handleSubmit(submit)}
            >
              <Controller
                control={control}
                name="days"
                render={({
                  field: { onBlur, onChange, ref, value },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    isInvalid={invalid}
                    onBlur={onBlur}
                    onChange={onChange}
                    validationBehavior="aria"
                    value={value}
                  >
                    <Label requiredHint>Days to add</Label>
                    <ClearableInput
                      inputMode="numeric"
                      onClear={() =>
                        setValue("days", "", { shouldValidate: true })
                      }
                      ref={ref}
                      showClearButton={!!value}
                    />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />

              <Controller
                control={control}
                name="reason"
                render={({ field, fieldState: { error } }) => (
                  <CreditReasonSelect
                    error={error?.message}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
              />

              <Controller
                control={control}
                name="note"
                render={({ field }) => (
                  <CreditNoteField
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    placeholder="Why this is being granted. Logged with the adjustment."
                    value={field.value}
                  />
                )}
              />

              {overview && (
                <ul
                  aria-label="Adjustment summary"
                  className="flex flex-col gap-xs rounded-xl border border-border-secondary bg-bg-secondary p-lg text-sm"
                >
                  <SummaryRow label="Current renewal">
                    {dateFormatter(overview.upcomingPayment.renewalDate)}
                  </SummaryRow>
                  <SummaryRow label="Days added">
                    {daysToAdd ? `+${daysToAdd}` : "-"}
                  </SummaryRow>
                  <li
                    aria-hidden="true"
                    className="my-xs border-t border-border-secondary"
                  />
                  <SummaryRow
                    label="New renewal"
                    valueClassName="text-text-brand-secondary"
                  >
                    {daysToAdd
                      ? dateFormatter(
                          addRenewalDays(
                            overview.upcomingPayment.renewalDate,
                            daysToAdd,
                          ),
                        )
                      : "-"}
                  </SummaryRow>
                  <SummaryRow label="Credit balance">
                    {`unchanged · ${formatCurrency(
                      overview.creditBalance.amount,
                      overview.creditBalance.currency,
                    )}`}
                  </SummaryRow>
                </ul>
              )}
            </form>
          )}
        </DialogBody>

        <DialogFooter className="px-xl pb-xl sm:border-t sm:border-border-secondary sm:px-3xl sm:pb-3xl">
          <Button
            className="sm:flex-1"
            color="gray"
            onPress={onClose}
            size="lg"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="sm:flex-1"
            form={formId}
            isDisabled={
              !!blockedMessage ||
              !formState.isValid ||
              adjustRenewalDate.isPending
            }
            isLoading={adjustRenewalDate.isPending}
            size="lg"
            type="submit"
          >
            Adjust renewal date
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

function SummaryRow({
  children,
  label,
  valueClassName = "text-text-primary",
}: {
  children: React.ReactNode;
  label: string;
  valueClassName?: string;
}) {
  return (
    <li className="flex items-center justify-between gap-md">
      <span className="text-text-tertiary">{label}</span>
      <span className={`font-semibold ${valueClassName}`}>{children}</span>
    </li>
  );
}
